// Presign contract for POST /api/file_uploads. The extension and content_type
// must match. .heic is image/heic and .heif is image/heif.

export const CONTENT_TYPES = {
	mov: 'video/quicktime',
	pdf: 'application/pdf',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	heic: 'image/heic',
	heif: 'image/heif',
	gif: 'image/gif',
	png: 'image/png',
	bmp: 'image/bmp',
	tiff: 'image/tiff',
	tif: 'image/tiff',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	txt: 'text/plain',
	csv: 'text/csv',
	swf: 'application/x-shockwave-flash',
	rtf: 'application/rtf',
	xlr: 'application/vnd.ms-works',
	xls: 'application/vnd.ms-excel',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	mp4: 'video/mp4',
	m4v: 'video/x-m4v',
	pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	ppt: 'application/vnd.ms-powerpoint',
	pptm: 'application/vnd.ms-powerpoint.presentation.macroenabled.12',
	svg: 'image/svg+xml',
};

const VEHICLE_KINDS = ['attachment', 'photo', 'mpi_item', 'appraisal'];

function flagOn(value) {
	return value === true || value === 'true' || value === '1';
}

export function contentTypeForFilename(filename) {
	const parts = String(filename || '').split('.');
	if (parts.length < 2) return null;
	const extension = parts.pop().toLowerCase();
	return CONTENT_TYPES[extension] || null;
}

const FILES_UPLOAD_KEY = 'recon_files_upload';

function blankDealership(row) {
	return !row || row.dealership === '' || row.dealership === null || row.dealership === undefined;
}

function lowestIdFirst(rows) {
	return rows.slice().sort((left, right) => {
		const leftId = Number(left && left.id);
		const rightId = Number(right && right.id);
		return (Number.isFinite(leftId) ? leftId : 0) - (Number.isFinite(rightId) ? rightId : 0);
	});
}

// The admin switch edits this row: dealership blank, for every rooftop on the database.
export function reconFilesUploadDatabaseValue(dealerSettings) {
	const list = Array.isArray(dealerSettings) ? dealerSettings : [];
	const chosen = lowestIdFirst(list.filter(row => row && row.key === FILES_UPLOAD_KEY && blankDealership(row)));
	if (!chosen.length || chosen[0].value === undefined) return '';
	return chosen[0].value;
}

// dealer_settings: a rooftop row wins, otherwise the blank dealership row
// for this database. Same order as DealerSettings.dealerSettingValue.
export function reconFilesUploadSetting(dealerSettings, subdomain) {
	const list = Array.isArray(dealerSettings) ? dealerSettings : [];
	const named = list.filter(row => row && row.key === FILES_UPLOAD_KEY);
	const forRooftop = lowestIdFirst(named.filter(row => row.dealership === subdomain));
	const chosen = forRooftop.length
		? forRooftop
		: lowestIdFirst(named.filter(blankDealership));
	if (!chosen.length || chosen[0].value === undefined) return '';
	return chosen[0].value;
}

// The recon_files_upload dealer setting decides, so each dealer database moves
// off the PHP file server on its own. A missing row stays on the PHP upload.
export function reconFilesUploadEnabled({ dealerSettings, subdomain } = {}) {
	return flagOn(reconFilesUploadSetting(dealerSettings, subdomain));
}

export function presignBody({ kind, filename, byteSize, vehicleId, userId }) {
	const contentType = contentTypeForFilename(filename);
	if (!contentType) return null;

	const body = {
		kind,
		filename,
		content_type: contentType,
		byte_size: byteSize,
	};

	if (VEHICLE_KINDS.indexOf(kind) !== -1) {
		body.vehicle_id = vehicleId;
	}
	if (kind === 'avatar') {
		body.user_id = userId;
	}
	return body;
}

// Share of bytes sent. Parts are { loaded, total } from the upload request.
export function uploadPercent(parts) {
	const list = parts || [];
	let total = 0;
	let loaded = 0;
	list.forEach((part) => {
		const size = Number(part && part.total) || 0;
		const sent = Number(part && part.loaded) || 0;
		total += size;
		loaded += size ? Math.min(sent, size) : sent;
	});
	if (!total) return 0;
	return Math.min(100, Math.round((loaded / total) * 100));
}

// Presigned S3 rejects the app's Authorization and dealer headers.
export function s3PutHeaders(signedHeaders) {
	return {
		...(signedHeaders || {}),
		Authorization: null,
		'X-DEALER': null,
		'X-DEALER-SUBDOMAIN': null,
		'X-USERNAME': null,
	};
}

// One fresh presign after an S3 403. Other PUT failures are not retried.
export async function uploadWithTicket(file, presign, put) {
	let ticket = await presign();
	try {
		await put(ticket, file);
		return ticket;
	} catch (error) {
		const status = error && error.response && error.response.status;
		if (status !== 403) throw error;
	}
	ticket = await presign();
	await put(ticket, file);
	return ticket;
}
